import { auth } from './firebase';

// API Configuration for Web
const API_ENDPOINTS = {
  // Base URL - Use Render backend for both dev and prod
  BASE_URL: 'https://lecha-backend-yffq.onrender.com',
  
  // For testing, you can override this with localhost if needed
  OVERRIDE_DEV_URL: 'https://lecha-backend-yffq.onrender.com', // Set to your ngrok URL for testing
};

// Get the base URL based on environment
export const getBaseUrl = () => {
  // If override is set and we're in development, use it
  if (process.env.NODE_ENV === 'development' && API_ENDPOINTS.OVERRIDE_DEV_URL) {
    return API_ENDPOINTS.OVERRIDE_DEV_URL;
  }
  
  // Otherwise always use the Render backend
  return API_ENDPOINTS.BASE_URL;
};

// Helper function to get auth headers
export const getAuthHeaders = async (contentType = 'application/json') => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('No authenticated user');
  }
  
  const token = await user.getIdToken();
  const headers = {
    'Authorization': `Bearer ${token}`
  };
  
  // Only add Content-Type if specified
  if (contentType) {
    headers['Content-Type'] = contentType;
  }
  
  return headers;
};

// Helper function to make authenticated API calls
export const apiCall = async (endpoint, options = {}) => {
  try {
    // Check if body is FormData - if so, don't set Content-Type (let browser set it with boundary)
    const isFormData = options.body instanceof FormData;
    
    // Determine content type - use custom headers if provided, but not for FormData
    const contentType = isFormData ? null : (options.headers?.['Content-Type'] || 'application/json');
    const authHeaders = await getAuthHeaders(contentType);
    
    // Merge headers properly, but remove Content-Type for FormData
      const headers = {
    'x-platform': 'web', // Identify requests as coming from web
    'ngrok-skip-browser-warning': 'true', // Skip ngrok warning page
    ...authHeaders,
    ...options.headers
  };
    
    // Remove Content-Type for FormData uploads
    if (isFormData && headers['Content-Type']) {
      delete headers['Content-Type'];
    }
    
    const url = `${getBaseUrl()}${endpoint}`;
    
    // Only log in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`API Call: ${options.method || 'GET'} ${url}`, isFormData ? '(FormData)' : '');
    }
    
    const response = await fetch(url, {
      ...options,
      headers
    });

    // Only log in development
    if (process.env.NODE_ENV === 'development') {
    console.log(`API Response: ${response.status} ${response.statusText}`);
    }

    // Try to parse response
    const text = await response.text();
    
    // Only log raw response in development
    if (process.env.NODE_ENV === 'development') {
      console.log('Raw response text:', text);
    }
    
    let data = null;
    if (text) {
      try {
        // Check if it's an SSE (Server-Sent Events) response
        // SSE responses should start with "data:" or have proper SSE format with newlines
        const isSSE = text.trim().startsWith('data:') || 
                     (text.includes('data:') && text.includes('\n\n') && 
                      text.split('\n').some(line => line.trim().startsWith('data:')));
        
        if (isSSE) {
          if (process.env.NODE_ENV === 'development') {
            console.log('Detected SSE response, parsing events...');
          }
          
          // Find all data events (matching mobile app logic)
          const dataEvents = text.split('\n\n')
            .filter(line => line.startsWith('data:'))
            .map(line => line.slice(5).trim()); // Remove 'data:' prefix
          
          if (process.env.NODE_ENV === 'development') {
            console.log('Found SSE events:', dataEvents.length);
          }
          
          // Process the last complete data event
          if (dataEvents.length > 0) {
            const lastEvent = dataEvents[dataEvents.length - 1];
            
            if (process.env.NODE_ENV === 'development') {
              console.log('Last SSE event:', lastEvent);
            }
            
            const parsedEvent = JSON.parse(lastEvent);
            
            if (process.env.NODE_ENV === 'development') {
              console.log('Parsed SSE event:', parsedEvent);
            }
            
            if (parsedEvent.status === 'complete' && parsedEvent.problem) {
              data = parsedEvent; // Return the complete event with problem data
            } else if (parsedEvent.status === 'error') {
              throw new Error(parsedEvent.error || 'Server processing error');
            } else {
              // Still processing or other status
              data = parsedEvent;
            }
          } else {
            throw new Error('No valid SSE events found');
          }
        } else {
          // Regular JSON response
          data = JSON.parse(text);
          if (process.env.NODE_ENV === 'development') {
            console.log('Parsed JSON data:', data);
          }
        }
    } catch (parseError) {
        console.error('Failed to parse response:', parseError);
        if (process.env.NODE_ENV === 'development') {
          console.error('Response text:', text);
        }
        // If it's not JSON or SSE, treat it as plain text
        data = { text: text };
      }
    }

    // Check if response is ok AFTER parsing
    if (!response.ok) {
      console.error(`API Error ${response.status}:`, data);
      
      // If we have a parsed error response, use it
      if (data && data.error) {
        throw new Error(data.error);
      } else if (data && data.message) {
        throw new Error(data.message);
      } else {
        throw new Error(`HTTP ${response.status}: ${text || response.statusText}`);
    }
    }

    return data;
  } catch (error) {
    console.error('API call failed:', error);
    throw error;
  }
};

// API endpoints
export const API_ROUTES = {
  // Authentication
  TEST_CONNECTION: '/api/test',
  
  // Recordings
  RECORDINGS: '/api/recordings',
  RECORDING: (id) => `/api/recordings/${id}`,
  
  // Transcription
  TRANSCRIBE: '/api/transcribe',
  TRANSCRIBE_YOUTUBE: '/api/transcribe-youtube',
  
  // Processing endpoints
  PROCESS_PDF: '/api/process-pdf',
  PROCESS_VIDEO: '/api/process-video',
  SOLVE_PROBLEM: '/api/solve-problem',
  GENERATE_FLASHCARDS: '/api/generate-flashcards',
  
  // User data
  USER_PROFILE: '/api/user/profile',
  USER_STATS: '/api/user/stats',
  USER_USAGE: '/api/user/usage',
  UPDATE_SUBSCRIPTION: '/api/user/update-subscription',
  RESET_USAGE: '/api/user/reset-usage',
  
  // Problems (Photo Tutor)
  PROBLEMS: '/api/problems',
  PROBLEM_IMAGE: (id) => `/api/problems/${id}/image`,
  
  // Flashcards & Quiz
  GENERATE_QUIZ: '/api/generate-quiz',
  
  // YouTube
  YOUTUBE_VIDEOS: '/api/youtube',
  YOUTUBE_VIDEO: (id) => `/api/youtube/${id}`,
  
  // PDFs
  PDFS: '/api/pdfs',
  PDF: (id) => `/api/pdfs/${id}`,
  
  // Subscriptions
  SUBSCRIPTION_STATUS: '/api/subscription/status',
  CREATE_CHECKOUT: '/api/subscription/checkout',
  CUSTOMER_PORTAL: '/api/subscription/portal',
  
  // Conversations (Chat Tutor)
  CONVERSATIONS: '/api/conversations',
  CONVERSATION: (id) => `/api/conversations/${id}`,
  CONVERSATION_MESSAGES: (id) => `/api/conversations/${id}/messages`,
  
  // Clone default recordings for new users
  CLONE_DEFAULT_RECORDING: '/api/clone-default-recording'
};

export default API_ENDPOINTS;